import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from 'i18next';
import apiClient from './api-client';
import {BASE_API} from '../config/apis';
import {APP_HEADERS} from '../config/appHeaders';

// Receipt scans (backend: draft -> pending -> approved / rejected):
//   POST scans/extract      photo + placeId -> the server reads the receipt
//                           (OCR) and makes a DRAFT
//   POST scans/:id/submit   user-checked price + fuel -> PENDING (admin review)
//   GET  scans/mine         the user's own scans

const EXTRACT_TIMEOUT_MS = 90000; // OCR can take a while

// The server's own words ("Scan not found", "...already approved") when it
// sent any, so the user sees a real reason.
const errorMessage = error =>
  error?.response?.data?.Message ||
  error?.Message ||
  error?.message ||
  'Something went wrong';

/**
 * Upload the receipt photo and let the server read it.
 * Uses fetch for the multipart upload (more reliable than axios for photos on
 * React Native).
 * @param {{uri: string, type: string, name: string}} file
 * @param {string} placeId Google place id of the station
 * @param {{latitude: number, longitude: number}|null} [location] where the user is now;
 *   sent so the server can check the user is at the station
 * @returns {Promise<{success: boolean, data?: object, status?: number, error?: string}>}
 *   data = the draft: {id, receiptUrl, fuelOption, unitPrice, receiptAt, ...}
 */
// Names of the position fields the server reads (confirm with the backend).
const LAT_FIELD = 'userLat';
const LNG_FIELD = 'userLng';

export const extractScan = async (file, placeId, location = null) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXTRACT_TIMEOUT_MS);
  try {
    const stored = await AsyncStorage.getItem('Data');
    const token = stored ? JSON.parse(stored)?.token : null;

    const formData = new FormData();
    formData.append('placeId', placeId);
    if (location?.latitude != null && location?.longitude != null) {
      formData.append(LAT_FIELD, String(location.latitude));
      formData.append(LNG_FIELD, String(location.longitude));
    }
    if (Platform.OS === 'web') {
      // The browser cannot upload a {uri, type, name} object (it would be sent
      // as the text "[object Object]"). It needs the real file data (a Blob).
      const blob = await (await fetch(file.uri)).blob();
      formData.append('file', blob, file.name || 'receipt.jpg');
    } else {
      formData.append('file', {uri: file.uri, type: file.type, name: file.name});
    }

    // No Content-Type: fetch adds multipart/form-data with its boundary.
    const response = await fetch(BASE_API + 'scans/extract', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...APP_HEADERS,
        'Accept-Language': i18n.language === 'dt' ? 'du' : 'en',
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
      },
      body: formData,
      signal: controller.signal,
    });

    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      // the body was not JSON
    }
    console.log('[scans/extract] status', response.status, json?.Message);

    if (!response.ok || json?.Success === 0 || !json?.Data) {
      return {
        success: false,
        status: response.status,
        error: json?.Message || `Upload failed (HTTP ${response.status})`,
      };
    }
    return {success: true, data: json.Data};
  } catch (error) {
    console.log('[scans/extract] failed', error);
    return {
      success: false,
      error:
        error.name === 'AbortError'
          ? 'Reading the receipt took too long. Please try again.'
          : error.message || 'Upload failed',
    };
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Send the checked draft for review.
 * @param {number} scanId  Data.id from extractScan
 * @param {{unitPrice: number, fuelOptionId: number}} values
 */
export const submitScan = async (scanId, {unitPrice, fuelOptionId}) => {
  try {
    const response = await apiClient.post(`scans/${scanId}/submit`, {
      unitPrice,
      fuelOptionId,
    });
    return {success: true, data: response.data?.Data, message: response.data?.Message};
  } catch (error) {
    return {
      success: false,
      status: error?.response?.status, // 409: this receipt was already approved
      error: errorMessage(error),
    };
  }
};

/**
 * The user's own scans.
 * @param {{status?: string, page?: number, limit?: number}} params
 *   status: leave out for all, or 'draft' | 'pending' | 'approved' | 'rejected'
 *   (confirm the exact values with the backend)
 * @returns {Promise<{success: boolean, scans?: object[], pagination?: object, error?: string}>}
 */
export const getMyScans = async ({status, page = 1, limit = 20} = {}) => {
  try {
    const response = await apiClient.get('scans/mine', {
      params: {...(status ? {status} : {}), page, limit},
    });
    const data = response.data?.Data;
    return {
      success: true,
      scans: data?.scans ?? [],
      pagination: data?.pagination,
    };
  } catch (error) {
    return {success: false, error: errorMessage(error)};
  }
};

/**
 * The fuel varieties the backend knows (GET fuel-options), each
 * {id, code, category, name, unit, sortOrder}. `id` is the fuelOptionId that
 * submitScan needs.
 * NOTE: confirm the path / `category` parameter with the Bruno request
 * "get-fuel-options".
 * @param {string} [category] 'petrol' | 'diesel' | 'lpg' | 'cng' | 'ev'
 */
export const getFuelOptions = async category => {
  try {
    const response = await apiClient.get('fuel-options', {
      params: category ? {category} : undefined,
    });
    const list = response.data?.Data;
    return {success: true, options: Array.isArray(list) ? list : []};
  } catch (error) {
    return {success: false, options: [], error: errorMessage(error)};
  }
};